import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Button,
  message,
} from "antd";
import { TruckOutlined, ToolOutlined } from "@ant-design/icons";
import {
  useCreateAccountingEntryMutation,
  useUpdateAccountingEntryMutation,
} from "../../store/api/accountingApi";
import type { AccountingEntryResponse } from "@OrianaTypes";
import type { DispatchResponse } from "@OrianaTypes";
import type { ServiceResponse } from "@OrianaTypes";

const EXPENSE_TYPE_DISPATCH = "dispatch_expense";
const EXPENSE_TYPE_COMMISSIONING = "commissioning_expense";

const expenseTypeOptions = [
  { value: EXPENSE_TYPE_DISPATCH, label: "Dispatch expense" },
  { value: EXPENSE_TYPE_COMMISSIONING, label: "Commissioning expense" },
];

const sectionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "10px 14px",
  borderRadius: "10px",
  marginBottom: "16px",
  marginTop: "8px",
  fontWeight: 600,
  fontSize: "13px",
};

const currencyStyle: React.CSSProperties = { width: "100%" };

interface AddExpenseModalProps {
  open: boolean;
  onClose: () => void;
  poId: string;
  dispatches?: DispatchResponse[];
  services?: ServiceResponse[];
  editRecord?: AccountingEntryResponse | null;
}

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  open,
  onClose,
  poId,
  dispatches = [],
  services = [],
  editRecord = null,
}) => {
  const [form] = Form.useForm();
  const isEditMode = !!editRecord;
  const expenseType = Form.useWatch("expenseType", form);

  const [createEntry, { isLoading: isCreating }] = useCreateAccountingEntryMutation();
  const [updateEntry, { isLoading: isUpdating }] = useUpdateAccountingEntryMutation();
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (!open) return;

    if (isEditMode && editRecord) {
      const hasDispatch = editRecord.dispatchExpenses != null && editRecord.dispatchExpenses !== "";
      const inferredType = hasDispatch ? EXPENSE_TYPE_DISPATCH : EXPENSE_TYPE_COMMISSIONING;

      form.setFieldsValue({
        expenseType: inferredType,
        dispatchId: editRecord.dispatchId,
        serviceId: editRecord.serviceId,
        dispatchExpenses:
          editRecord.dispatchExpenses != null ? parseFloat(editRecord.dispatchExpenses) : undefined,
        dispatchExpensesRemark: editRecord.dispatchExpensesRemark,
        commissioningExpenses:
          editRecord.commissioningExpenses != null
            ? parseFloat(editRecord.commissioningExpenses)
            : undefined,
        commissioningExpensesRemark: editRecord.commissioningExpensesRemark,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        expenseType: EXPENSE_TYPE_DISPATCH,
      });
    }
  }, [open, isEditMode, editRecord, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (values.expenseType === EXPENSE_TYPE_DISPATCH) {
        const payload = {
          dispatchId: values.dispatchId,
          dispatchExpenses: values.dispatchExpenses,
          dispatchExpensesRemark: values.dispatchExpensesRemark,
        };
        if (isEditMode && editRecord) {
          await updateEntry({
            id: editRecord.id,
            data: payload,
            poId,
          }).unwrap();
        } else {
          await createEntry({
            poId,
            moduleType: "order",
            ...payload,
          }).unwrap();
        }
        message.success("Dispatch expense saved successfully");
      } else {
        const payload = {
          serviceId: values.serviceId,
          commissioningExpenses: values.commissioningExpenses,
          commissioningExpensesRemark: values.commissioningExpensesRemark,
        };
        if (isEditMode && editRecord) {
          await updateEntry({
            id: editRecord.id,
            data: payload,
            poId,
          }).unwrap();
        } else {
          await createEntry({
            poId,
            moduleType: "order",
            ...payload,
          }).unwrap();
        }
        message.success("Commissioning expense saved successfully");
      }

      form.resetFields();
      onClose();
    } catch (err) {
      if (err && typeof err === "object" && "errorFields" in err) return;
      message.error("Failed to save expense. Please try again.");
    }
  };

  const dispatchOptions = dispatches.map((d) => ({
    value: d.dispatchId,
    label: `Dispatch #${d.dispatchId}${d.taxInvoiceNumber ? ` — ${d.taxInvoiceNumber}` : ""}`,
  }));

  const serviceOptions = services.map((s) => ({
    value: s.serviceId,
    label: `Service #${s.serviceId} (Dispatch #${s.dispatchId})`,
  }));

  return (
    <Modal
      title={
        <span style={{ fontWeight: 700, fontSize: "16px" }}>
          {isEditMode ? "Edit Expense Entry" : "Add Expense"}
        </span>
      }
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      width={560}
      destroyOnClose
      styles={{ body: { padding: "16px 24px" } }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();
            onClose();
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
          style={{ background: "#71a241", borderColor: "#71a241" }}
        >
          {isEditMode ? "Update" : "Save Entry"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item name="expenseType" label="Expense type" rules={[{ required: true }]}>
          <Select placeholder="Select type" options={expenseTypeOptions} />
        </Form.Item>

        {expenseType === EXPENSE_TYPE_DISPATCH && (
          <>
            <div
              style={{
                ...sectionStyle,
                background: "linear-gradient(135deg, #f85f7a15 0%, #fc466b15 100%)",
                border: "1px solid #f85f7a30",
                color: "#9b1c1c",
              }}
            >
              <TruckOutlined style={{ fontSize: "16px" }} />
              <span>Dispatch expense</span>
            </div>
            <Form.Item
              name="dispatchId"
              label="Dispatch"
              rules={[{ required: true, message: "Please select a dispatch" }]}
            >
              <Select
                placeholder="Select dispatch"
                options={dispatchOptions}
                allowClear={false}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="dispatchExpenses"
                  label="Amount (₹)"
                  rules={[{ required: true, message: "Enter expense amount" }]}
                >
                  <InputNumber
                    style={currencyStyle}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="dispatchExpensesRemark" label="Remark">
                  <Input.TextArea rows={2} placeholder="Optional remark" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {expenseType === EXPENSE_TYPE_COMMISSIONING && (
          <>
            <div
              style={{
                ...sectionStyle,
                background: "linear-gradient(135deg, #f7971e15 0%, #ffd20015 100%)",
                border: "1px solid #f7971e30",
                color: "#92400e",
              }}
            >
              <ToolOutlined style={{ fontSize: "16px" }} />
              <span>Commissioning expense</span>
            </div>
            <Form.Item
              name="serviceId"
              label="Commissioning (Service)"
              rules={[{ required: true, message: "Please select a service" }]}
            >
              <Select
                placeholder="Select service / commissioning"
                options={serviceOptions}
                allowClear={false}
              />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="commissioningExpenses"
                  label="Amount (₹)"
                  rules={[{ required: true, message: "Enter expense amount" }]}
                >
                  <InputNumber
                    style={currencyStyle}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="commissioningExpensesRemark" label="Remark">
                  <Input.TextArea rows={2} placeholder="Optional remark" />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default AddExpenseModal;
